import React from "react";

import { css, cx } from "@emotion/css";
import { createColumnHelper, getCoreRowModel, useReactTable } from "@tanstack/react-table";

import { useStyles } from "@/hooks/useStyles.ts";
import { CarePlan } from "@/models/CarePlanModel.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { DescriptionColumn } from "@/uiKit/molecules/CarePlan/CarePlansTableColumns/DescriptionColumn.tsx";
import { PeriodColumn } from "@/uiKit/molecules/CarePlan/CarePlansTableColumns/PeriodColumn.tsx";
import { PrescriptionColumn } from "@/uiKit/molecules/CarePlan/CarePlansTableColumns/PrescriptionColumn.tsx";
import { TelemonitoringCriteriaColumn } from "@/uiKit/molecules/CarePlan/CarePlansTableColumns/TelemonitoringCriteriaColumn.tsx";
import { TelemonitoringTierColumn } from "@/uiKit/molecules/CarePlan/CarePlansTableColumns/TelemonitoringTierColumn.tsx";
import { CarePlansTableHeader } from "@/uiKit/molecules/CarePlan/CarePlansTableHeader.tsx";
import { MuiTable } from "@/uiKit/Table.tsx";

export type CarePlansTableProps = {
  carePlans: CarePlan[];
  className?: string;
};

const columnHelper = createColumnHelper<CarePlan>();

const columnDefinitions = {
  default: {
    size: 100,
    minSize: 0,
    maxSize: 100,
  },
  prescription: columnHelper.accessor("prescription", {
    cell: PrescriptionColumn,
    header: () => <CarePlansTableHeader title="carePlans.historyPrescriptionTitle" />,
    maxSize: 20,
  }),
  period: columnHelper.display({
    id: "period",
    cell: PeriodColumn,
    header: () => <CarePlansTableHeader title="carePlans.historyPeriodTitle" />,
    maxSize: 20,
  }),
  telemonitoringTier: columnHelper.accessor("telemonitoringTier", {
    cell: TelemonitoringTierColumn,
    header: () => <CarePlansTableHeader title="carePlans.historyTelemonitoringTierTitle" />,
    maxSize: 10,
  }),
  telemonitoringCriteria: columnHelper.accessor("telemonitoringCriteria", {
    cell: TelemonitoringCriteriaColumn,
    header: () => <CarePlansTableHeader title="carePlans.historyTelemonitoringCriteriaTitle" />,
    maxSize: 25,
  }),
  description: columnHelper.accessor("description", {
    cell: DescriptionColumn,
    header: () => <CarePlansTableHeader title="carePlans.historyObjectivesTitle" />,
    maxSize: 25,
  }),
};

export const CarePlansTable: React.FC<CarePlansTableProps> = ({ carePlans, className }) => {
  const styles = useStyles(makeStyles);

  const table = useReactTable<CarePlan>({
    columns: [
      columnDefinitions.prescription,
      columnDefinitions.period,
      columnDefinitions.telemonitoringTier,
      columnDefinitions.telemonitoringCriteria,
      columnDefinitions.description,
    ],
    data: carePlans,
    getCoreRowModel: getCoreRowModel(),
    enableRowSelection: false,
  });

  return (
    <div className={cx(styles.container, className)}>
      <MuiTable
        table={table}
        classes={{
          headerCell: styles.header,
          cell: styles.cell,
        }}
      />
    </div>
  );
};

const makeStyles = (theme: Theme) => ({
  container: css({}),
  header: css({
    padding: theme.spacing(2, 8),
    whiteSpace: "nowrap",
    minWidth: 0,
    overflow: "clip",
  }),
  cell: css({
    height: theme.spacing(22),
    padding: theme.spacing(2, 8),
    whiteSpace: "nowrap",
    minWidth: 0,
    overflow: "clip",
  }),
});
